import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { invoiceService } from '../services/invoiceService';

const fmt = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return `£${parseFloat(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const calcGross = (value, vatRate) => {
  const net = parseFloat(value);
  const rate = vatRate ? parseFloat(vatRate.rate) : null;
  if (isNaN(net)) return null;
  if (!rate) return net;
  return net * (1 + rate / 100);
};

const calcVatAmount = (value, vatRate) => {
  const net = parseFloat(value);
  const rate = vatRate ? parseFloat(vatRate.rate) : null;
  if (isNaN(net) || !rate) return null;
  return net * (rate / 100);
};

const fmtDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
};

const STATUS_COLORS = {
  'Not Sent':       '#6b7280',
  'Sent':           '#1d4ed8',
  'Paid':           '#15803d',
  'Partially Paid': '#0f766e',
  'Overdue':        '#b91c1c',
  'Cancelled':      '#92400e',
};

const PrintInvoice = () => {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    invoiceService.getInvoice(id)
      .then(res => setInvoice(res.data))
      .catch(() => setError('Invoice not found.'));
  }, [id]);

  useEffect(() => {
    if (invoice) {
      document.title = `Invoice #${invoice.invoice_number}`;
    }
  }, [invoice]);

  const handlePrint = () => window.print();

  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', fontFamily: 'sans-serif' }}>
        <p style={{ color: '#b91c1c' }}>{error}</p>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', fontFamily: 'sans-serif', color: '#6b7280' }}>
        Loading…
      </div>
    );
  }

  const job = invoice.job;
  const site = job?.site;
  const customer = site?.customer;
  const vatRate = job?.vat_rate;
  const netValue = parseFloat(job?.value);
  const vatAmount = calcVatAmount(job?.value, vatRate);
  const gross = calcGross(job?.value, vatRate);
  const amountDue = gross !== null
    ? Math.max(0, gross - (invoice.amount_paid != null ? parseFloat(invoice.amount_paid) : 0))
    : null;
  const status = invoice.status || 'Not Sent';
  const statusColor = STATUS_COLORS[status] || '#6b7280';

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; }
          @page { margin: 1.5cm; size: A4 portrait; }
        }
        body { font-family: 'Segoe UI', Arial, sans-serif; background: #f3f4f6; margin: 0; }
      `}</style>

      {/* Toolbar – hidden on print */}
      <div className="no-print" style={{ background: '#1e293b', padding: '12px 24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span style={{ color: '#94a3b8', fontSize: '14px', flex: 1 }}>Invoice #{invoice.invoice_number}</span>
        <button
          onClick={handlePrint}
          style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 20px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
        >
          🖨 Print
        </button>
        <button
          onClick={() => window.close()}
          style={{ background: '#374151', color: '#d1d5db', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '14px', cursor: 'pointer' }}
        >
          Close
        </button>
      </div>

      {/* Invoice page */}
      <div style={{ maxWidth: '800px', margin: '32px auto 64px', background: '#fff', borderRadius: '8px', boxShadow: '0 4px 24px rgba(0,0,0,0.1)', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ background: '#1e293b', padding: '40px 48px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ color: '#fff', fontSize: '32px', fontWeight: 800, letterSpacing: '-0.5px' }}>INVOICE</div>
            {invoice.user?.company_name && (
              <div style={{ color: '#e2e8f0', fontSize: '17px', fontWeight: 600, marginTop: '6px' }}>{invoice.user.company_name}</div>
            )}
            <div style={{ color: '#94a3b8', fontSize: '15px', marginTop: '4px' }}>#{invoice.invoice_number}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            {invoice.job?.customer_po && (
              <div style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '6px' }}>Your PO: {invoice.job.customer_po}</div>
            )}
            <div style={{ color: '#94a3b8', fontSize: '13px', marginTop: '8px' }}>
              Issued: {fmtDate(invoice.created_at)}
            </div>
            {invoice.date_paid && (
              <div style={{ color: '#94a3b8', fontSize: '13px', marginTop: '2px' }}>
                Paid: {fmtDate(invoice.date_paid)}
              </div>
            )}
          </div>
        </div>

        <div style={{ padding: '40px 48px' }}>

          {/* Addresses */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', marginBottom: '40px' }}>
            {/* Customer / Bill To */}
            <div>
              <div style={{ fontSize: '10px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>Bill To</div>
              {customer ? (
                <>
                  <div style={{ fontWeight: 700, fontSize: '16px', color: '#111827' }}>{customer.name}</div>
                  {customer.street_address && <div style={{ color: '#374151', fontSize: '14px', marginTop: '4px' }}>{customer.street_address}</div>}
                  {customer.town && <div style={{ color: '#374151', fontSize: '14px' }}>{customer.town}</div>}
                  {customer.county && <div style={{ color: '#374151', fontSize: '14px' }}>{customer.county}</div>}
                  {customer.postcode && <div style={{ color: '#374151', fontSize: '14px' }}>{customer.postcode}</div>}
                  {customer.country && <div style={{ color: '#374151', fontSize: '14px' }}>{customer.country}</div>}
                </>
              ) : (
                <div style={{ color: '#9ca3af', fontSize: '14px' }}>—</div>
              )}
            </div>

            {/* Site */}
            <div>
              <div style={{ fontSize: '10px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>Site</div>
              {site ? (
                <>
                  <div style={{ fontWeight: 700, fontSize: '16px', color: '#111827' }}>{site.name}</div>
                  {site.street_address && <div style={{ color: '#374151', fontSize: '14px', marginTop: '4px' }}>{site.street_address}</div>}
                  {site.town && <div style={{ color: '#374151', fontSize: '14px' }}>{site.town}</div>}
                  {site.county && <div style={{ color: '#374151', fontSize: '14px' }}>{site.county}</div>}
                  {site.postcode && <div style={{ color: '#374151', fontSize: '14px' }}>{site.postcode}</div>}
                  {site.country && <div style={{ color: '#374151', fontSize: '14px' }}>{site.country}</div>}
                </>
              ) : (
                <div style={{ color: '#9ca3af', fontSize: '14px' }}>—</div>
              )}
            </div>
          </div>

          {/* Job details table */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '32px' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Job</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Description</th>
                <th style={{ textAlign: 'right', padding: '12px 16px', fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Net</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '16px 16px', fontSize: '14px', fontWeight: 600, color: '#111827', verticalAlign: 'top' }}>
                  {job?.title || '—'}
                </td>
                <td style={{ padding: '16px 16px', fontSize: '14px', color: '#374151', verticalAlign: 'top', maxWidth: '280px' }}>
                  {job?.description || <span style={{ color: '#9ca3af' }}>—</span>}
                </td>
                <td style={{ padding: '16px 16px', fontSize: '14px', fontWeight: 600, color: '#111827', textAlign: 'right', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                  {isNaN(netValue) ? '—' : fmt(netValue)}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Totals */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ width: '280px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: '14px', color: '#374151' }}>
                <span>Net</span>
                <span>{isNaN(netValue) ? '—' : fmt(netValue)}</span>
              </div>
              {vatAmount !== null && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: '14px', color: '#374151' }}>
                  <span>VAT ({parseFloat(vatRate.rate)}%)</span>
                  <span>{fmt(vatAmount)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '2px solid #e5e7eb', fontSize: '16px', fontWeight: 700, color: '#111827' }}>
                <span>Total</span>
                <span>{fmt(gross ?? netValue)}</span>
              </div>
              {invoice.amount_paid != null && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: '14px', color: '#15803d' }}>
                  <span>Amount Paid</span>
                  <span>{fmt(invoice.amount_paid)}</span>
                </div>
              )}
              {amountDue !== null && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', fontSize: '17px', fontWeight: 800, color: amountDue === 0 ? '#15803d' : '#b91c1c' }}>
                  <span>Amount Due</span>
                  <span>{fmt(amountDue)}</span>
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Footer */}
        <div style={{ background: '#f9fafb', borderTop: '1px solid #e5e7eb', padding: '20px 48px' }}>
          {invoice.user?.company_address && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '4px' }}>Company Address</div>
              <div style={{ fontSize: '13px', color: '#374151', whiteSpace: 'pre-line', lineHeight: '1.6' }}>{invoice.user.company_address}</div>
            </div>
          )}
          {invoice.user?.payment_details && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '4px' }}>Payment Details</div>
              <div style={{ fontSize: '13px', color: '#374151', whiteSpace: 'pre-line', lineHeight: '1.6' }}>{invoice.user.payment_details}</div>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', color: '#9ca3af' }}>Invoice #{invoice.invoice_number}</span>
            <span style={{ fontSize: '12px', color: '#9ca3af' }}>Generated {new Date().toLocaleDateString('en-GB')}</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrintInvoice;
